import { useState, useRef, useEffect } from 'react';
import { Icon } from './Icons.jsx';
import { MobHeader, FooterBar } from './Stepper.jsx';
import CameraCapture from './CameraCapture.jsx';
import ImagePreviewModal from './ImagePreviewModal.jsx';
import SourceTag from './SourceTag.jsx';
import { uploadPrescription } from '../api/orders.js';
import { getCatalog } from '../api/labs.js';
import { brl } from '../utils/format.js';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024;

// Fases mostradas durante o envio. 'upload' é progresso real (bytes enviados);
// 'scan' e 'identify' são as etapas do backend (Vision transcreve, Claude estrutura),
// que o servidor faz numa só resposta — avançamos por estimativa de tempo.
const PHASE_LABELS = {
  upload: 'Enviando o arquivo',
  scan: 'Digitalizando o pedido',
  identify: 'Identificando os exames',
};

// Junta os exames de todos os pedidos numa lista única, sem duplicar por código,
// guardando em `sources` os ids dos pedidos que pediram cada exame. Exames
// adicionados manualmente (avulso ou associados a um pedido já incluído) são
// preservados — são eles que `sources.length`/`fromOrder` não cobrem aqui.
function aggregateExams(uploads, prevExams = []) {
  const manual = (prevExams || []).filter(e => e.fromOrder === false || e.manual === true);
  const byKey = new Map();
  for (const up of uploads) {
    for (const e of up.exams || []) {
      const key = e.code || `na:${e.name}`;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.sources.includes(up.id)) existing.sources.push(up.id);
      } else {
        byKey.set(key, { ...e, fromOrder: true, sources: [up.id] });
      }
    }
  }
  return [...byKey.values(), ...manual];
}

export default function Step1Upload({ state, setState, onNext, onBack, preselectedExam, autoPrescription }) {
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef();
  const phaseTimer = useRef(null);
  const addOptionsRef = useRef(null);

  // Árvore de decisão — navegação local, não persistida entre montagens do
  // componente. Como o wizard (App.jsx) desmonta este componente ao trocar de
  // passo, os valores iniciais abaixo detectam se já existe carrinho/upload
  // (state.exams/state.uploads, que são persistidos no App) para reabrir
  // direto na visão de carrinho ao invés da pergunta inicial.
  // null | 'rx-ask' | 'rx-associate' | 'rx-new' | 'avulso' | 'done'
  // 'done': caminho concluído (pedido médico enviado); não mostra painel
  // nenhum, só o carrinho — distinto de null, que reabre a pergunta inicial.
  const hasExistingProgress = (state.exams?.length > 0) || (state.uploads?.length > 0);
  const [docMode, setDocMode] = useState(() => (hasExistingProgress ? 'done' : (autoPrescription ? 'rx-new' : null)));
  const [associateTarget, setAssociateTarget] = useState(null); // id do upload escolhido p/ "associar"
  // Painel "Como deseja adicionar exames?" some assim que o usuário escolhe
  // um caminho (docMode deixa de ser null); reabre ao clicar em "+ Adicionar"
  // no cabeçalho do carrinho (ver CartBar), mesmo com docMode já definido.
  const [showAddOptions, setShowAddOptions] = useState(false);
  // Busca de exame avulso: some quando o exame já veio pré-selecionado (botão
  // "Comprar") e essa é a 1ª escolha de "avulso" — não há o que buscar, o
  // exame já está no carrinho. Reaparece se o usuário reabrir "+ Adicionar"
  // e escolher "avulso" de novo, para incluir mais exames.
  const [avulsoSearchOpen, setAvulsoSearchOpen] = useState(false);
  // Uma vez que o usuário escolheu um caminho (pedido médico ou avulso) pela
  // 1ª vez, o carrinho do exame pré-selecionado nunca mais deve se esconder —
  // mesmo que docMode volte a null depois (reset após upload, "Fechar" na
  // busca avulsa etc.). Só fica oculto antes da 1ª escolha.
  const [hasChosenPath, setHasChosenPath] = useState(hasExistingProgress || !!autoPrescription);
  // Confirmação exibida ao clicar Continuar quando há exames "não oferecidos"
  // no carrinho — avisa que serão retirados antes de seguir para o próximo passo.
  const [confirmRemoval, setConfirmRemoval] = useState(false);

  const uploads = state.uploads || [];
  const hasFiles = uploads.length > 0;
  const cart = state.exams || [];
  const offCount = cart.filter(e => e.available === false).length;
  const doctorNames = [...new Set(uploads.map(u => u.doctor?.name).filter(Boolean))];

  // Semeia o exame pré-selecionado (veio de um botão "Comprar") no carrinho,
  // uma única vez.
  useEffect(() => {
    if (!preselectedExam) return;
    setState(s => {
      const exams = s.exams || [];
      if (exams.some(e => e.code === preselectedExam.code)) return s;
      return { ...s, exams: [{ ...preselectedExam, fromOrder: false, available: true, sources: [] }, ...exams] };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inCart = (code) => cart.some(e => e.code === code);
  const addToCart = (exam, opts = {}) => {
    setState(s => {
      const exams = s.exams || [];
      if (exams.some(e => e.code === exam.code)) return s;
      const entry = {
        code: exam.code, name: exam.name, price: exam.price,
        fromOrder: !!opts.fromOrder, manual: !!opts.manual, available: true,
        sources: opts.sourceId ? [opts.sourceId] : [],
      };
      return { ...s, exams: [...exams, entry] };
    });
  };
  const removeFromCart = (code) => {
    setState(s => ({ ...s, exams: (s.exams || []).filter(e => e.code !== code) }));
  };

  const sourceVariant = (e) => {
    if (e.fromOrder) {
      if (e.available === false) return 'unrecognized';
      return e.manual ? 'associated' : 'recognized';
    }
    return 'avulso';
  };
  const linkedOrderCount = (e) => e.sources?.length || 0;

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError('Formato inválido. Use PDF, JPG ou PNG.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setFileError('Arquivo muito grande. Máximo: 10 MB.');
      return;
    }
    setFileError(null);

    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setUploading(true);
    setPhase('upload');
    setUploadPct(0);
    let advanced = false;
    try {
      const result = await uploadPrescription(file, {
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploadPct(pct);
          // Upload concluído: o backend agora transcreve (Vision) e depois estrutura (Claude)
          if (pct >= 100 && !advanced) {
            advanced = true;
            setPhase('scan');
            phaseTimer.current = setTimeout(() => setPhase('identify'), 3000);
          }
        },
      });
      const upload = {
        id: (crypto.randomUUID?.() ?? String(Date.now())),
        name: file.name,
        size: `${Math.round(file.size / 1024)} KB`,
        isPdf: file.type === 'application/pdf',
        previewUrl,
        fileUrl: result.prescription_url || null,  // URL no storage (null até o storage ser configurado)
        ocr: result.ocr || null,                   // OCR bruto, para auditoria (ocr_json)
        doctor: result.ocr.doctor,
        exams: result.ocr.exams || [],
        medications: result.ocr.medications || [],
      };
      setState(s => {
        const next = [...(s.uploads || []), upload];
        return { ...s, uploads: next, exams: aggregateExams(next, s.exams) };
      });
      // Pedido processado e exames já refletidos no carrinho acima — sai da
      // tela de upload (em vez de mantê-la, ou o resumo de OCR, permanentemente
      // visível). Não volta a `null`: isso reabriria a pergunta "pedido médico
      // / avulso", já respondida; "done" só esconde este painel, mesmo depois
      // de mais de um upload ("+ Adicionar" reabre a pergunta se precisar).
      setDocMode('done');
    } catch (err) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      // Só este arquivo falhou — os pedidos já enviados continuam intactos
      const timedOut = err?.code === 'ECONNABORTED';
      setFileError(timedOut
        ? 'A leitura demorou mais que o esperado. Tente novamente ou use uma foto mais nítida.'
        : 'Não foi possível processar o pedido. Verifique sua conexão e tente novamente.');
    } finally {
      clearTimeout(phaseTimer.current);
      setUploading(false);
      setPhase(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Foto capturada pela câmera: fecha o overlay e processa como um upload comum.
  const handleCapture = (file) => {
    setShowCamera(false);
    handleFile(file);
  };

  const removeUpload = (id) => {
    const target = uploads.find(u => u.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    const next = uploads.filter(u => u.id !== id);
    // Remove da lista agregada os exames que vinham só deste pedido
    setState(s => ({ ...s, uploads: next, exams: aggregateExams(next, s.exams) }));
    setFileError(null);
  };

  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files?.[0]); };

  const addLabel = uploading
    ? `${PHASE_LABELS[phase]}${phase === 'upload' ? ` ${uploadPct}%` : '…'}`
    : null;

  const total = cart.reduce((s, e) => s + (e.price || 0), 0);
  const availableCart = cart.filter(e => e.available !== false);
  const unavailableCart = cart.filter(e => e.available === false);
  const canNext = availableCart.length > 0;

  const handleContinue = () => {
    if (unavailableCart.length > 0) setConfirmRemoval(true);
    else onNext();
  };
  const confirmRemovalAndContinue = () => {
    setState(s => ({ ...s, exams: (s.exams || []).filter(e => e.available !== false) }));
    setConfirmRemoval(false);
    onNext();
  };

  const goToPrescriptionFlow = () => {
    setShowAddOptions(false);
    setHasChosenPath(true);
    setDocMode(uploads.length > 0 ? 'rx-ask' : 'rx-new');
  };

  // Fecha a busca avulsa. Com exame pré-selecionado, a compra já está
  // decidida ("avulso") — fechar só esconde a busca, sem reabrir a pergunta.
  // Sem pré-seleção, não há outro estado de repouso: fechar volta à pergunta.
  const closeAvulsoSearch = () => {
    if (preselectedExam) setAvulsoSearchOpen(false);
    else setDocMode(null);
  };

  // Ao abrir o painel pelo "+ Adicionar" do carrinho, rola a tela até as opções —
  // a lista de exames acima pode ser longa e deixá-las fora da área visível.
  useEffect(() => {
    if (showAddOptions) addOptionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showAddOptions]);

  if (confirmRemoval) {
    return (
      <>
        <MobHeader title="Exames não oferecidos" onBack={() => setConfirmRemoval(false)}/>
        <div style={{ flex: 1, minHeight: 0, padding: '18px 16px', overflowY: 'auto', background: 'var(--bg)' }}>
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            {unavailableCart.length > 1
              ? 'Os exames abaixo não são oferecidos e serão retirados da sua compra:'
              : 'O exame abaixo não é oferecido e será retirado da sua compra:'}
          </p>
          <div className="card" style={{ padding: 14, marginTop: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {unavailableCart.map(e => (
                <div key={e.code} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon.flask size={15} color="var(--text-muted)"/>
                  <span title={e.name} style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--midnight)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.name}
                  </span>
                  <SourceTag variant="unrecognized"/>
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: 14, background: 'var(--brand-primary-soft)', borderRadius: 12, marginTop: 16, display: 'flex', gap: 10 }}>
            <Icon.info size={18} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: 2 }}/>
            <div style={{ fontSize: 13, color: 'var(--midnight)' }}>
              Você pode continuar sem eles ou voltar para revisar sua compra.
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 16px 20px', background: 'var(--white)', borderTop: '1px solid var(--border)', position: 'sticky', bottom: 0, zIndex: 10, display: 'flex', gap: 10 }}>
          <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setConfirmRemoval(false)}>Voltar</button>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={confirmRemovalAndContinue}>OK</button>
        </div>
      </>
    );
  }

  return (
    <>
      <MobHeader title="Definir Serviços" onBack={onBack}/>
      <div style={{ flex: 1, minHeight: 0, padding: '18px 16px', overflowY: 'auto', background: 'var(--bg)' }}>

        {!(preselectedExam && docMode === null && !hasChosenPath) && (
          <CartBar
            cart={cart} total={total}
            sourceVariant={sourceVariant} linkedOrderCount={linkedOrderCount}
            onRemove={removeFromCart}
            showAdd={showAddOptions}
            onToggleAdd={() => setShowAddOptions(v => !v)}
          />
        )}

        {(docMode === null || showAddOptions) && (
          <div ref={addOptionsRef}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginTop: 20 }}>Como deseja adicionar exames?</h2>
            <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>
              Alguns exames exigem pedido médico para serem realizados no laboratório.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={goToPrescriptionFlow}>
                <Icon.file size={18} color="var(--white)"/> Adicionar por pedido médico
              </button>
              <button className="btn btn-outline" onClick={() => { setAvulsoSearchOpen(showAddOptions); setShowAddOptions(false); setHasChosenPath(true); setDocMode('avulso'); }}>
                <Icon.flask size={18}/> Adicionar por compra avulsa
              </button>
            </div>
          </div>
        )}

        {docMode === 'rx-ask' && (
          <>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginTop: 20 }}>Esse exame pertence a um pedido já incluído?</h2>
            <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>
              Você já anexou {uploads.length > 1 ? 'pedidos médicos' : 'um pedido médico'} nesta compra. Associe o exame a um deles ou envie um novo pedido.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={() => { setAssociateTarget(uploads.length === 1 ? uploads[0].id : null); setDocMode('rx-associate'); }}>
                <Icon.search size={18} color="var(--white)"/> Associar ao pedido médico incluído
              </button>
              <button className="btn btn-outline" onClick={() => setDocMode('rx-new')}>
                <Icon.upload size={18}/> É um novo pedido
              </button>
            </div>
            <button onClick={() => setDocMode(null)} style={{ marginTop: 14, fontSize: 13, color: 'var(--brand-primary)', fontWeight: 600, display: 'block', margin: '14px auto 0' }}>
              Voltar
            </button>
          </>
        )}

        {docMode === 'rx-associate' && (
          <div style={{ marginTop: 20 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>Associar exame a um pedido incluído</h2>
            {uploads.length > 1 && (
              <>
                <p style={{ color: 'var(--text-muted)', marginTop: 4, marginBottom: 12, fontSize: 14 }}>Escolha o pedido médico:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {uploads.map(u => (
                    <button key={u.id} onClick={() => setAssociateTarget(u.id)} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '11px 12px', borderRadius: 10,
                      border: '1px solid ' + (associateTarget === u.id ? 'var(--brand-primary)' : 'var(--border)'),
                      background: associateTarget === u.id ? 'var(--brand-primary-soft)' : 'var(--white)', textAlign: 'left',
                    }}>
                      <span style={{ fontSize: 13.5, fontWeight: 500 }}>{u.doctor?.name || u.name}</span>
                      {associateTarget === u.id && <Icon.check size={16} color="var(--brand-primary)"/>}
                    </button>
                  ))}
                </div>
              </>
            )}
            {associateTarget && (
              <div style={{ marginTop: 16 }}>
                <ExamSearchInline cart={cart} onAdd={(exam) => addToCart(exam, { fromOrder: true, manual: true, sourceId: associateTarget })}/>
              </div>
            )}
            <button onClick={() => setDocMode('rx-ask')} style={{ marginTop: 14, fontSize: 13, color: 'var(--brand-primary)', fontWeight: 600 }}>
              Voltar
            </button>
          </div>
        )}

        {docMode === 'avulso' && !(preselectedExam && !avulsoSearchOpen) && (
          <div style={{ marginTop: 20 }}>
            <p style={{ color: 'var(--text-muted)', marginTop: 4, marginBottom: 14, fontSize: 14 }}>
              Busque os exames que deseja adicionar como compra avulsa (sem pedido médico).
            </p>
            <ExamSearchInline cart={cart} onAdd={(exam) => addToCart(exam, { fromOrder: false })} onClose={closeAvulsoSearch}/>
            <button onClick={closeAvulsoSearch} style={{ marginTop: 14, fontSize: 13, color: 'var(--brand-primary)', fontWeight: 600, display: 'block', marginLeft: 'auto' }}>
              Fechar
            </button>
          </div>
        )}

        {docMode === 'rx-new' && (
          <div style={{ marginTop: 20 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>Envie seu pedido médico</h2>
            <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 14 }}>
              Pode enviar mais de um pedido — juntamos todos os exames num só agendamento.
            </p>

            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])}/>

            {showCamera && (
              <CameraCapture
                onCapture={handleCapture}
                onClose={() => setShowCamera(false)}
                onPickFile={() => { setShowCamera(false); fileRef.current?.click(); }}
              />
            )}

            {preview && (
              <ImagePreviewModal src={preview.src} title={preview.title} onClose={() => setPreview(null)}/>
            )}

            {!hasFiles ? (
              <>
                <div
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  onClick={() => fileRef.current?.click()}
                  style={{
                    marginTop: 16,
                    padding: '36px 20px',
                    border: `2px dashed ${isDragging ? 'var(--brand-primary)' : 'var(--border-strong)'}`,
                    borderRadius: 14,
                    background: isDragging ? 'var(--brand-primary-soft)' : 'var(--marble-soft)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--white)', margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px var(--overlay-navy-06)' }}>
                    <Icon.upload size={26} color="var(--brand-primary)" strokeWidth={2}/>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15 }}>
                    {isDragging ? 'Solte o arquivo aqui' : 'Arraste ou clique para enviar'}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>PDF, JPG ou PNG · até 10 MB</div>
                </div>

                {fileError && (
                  <div role="alert" style={{ marginTop: 10, padding: '10px 14px', background: 'var(--primary-tint)', borderRadius: 10, color: 'var(--brand-primary)', fontSize: 13, fontWeight: 600 }}>
                    {fileError}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={() => setShowCamera(true)} disabled={uploading}>
                    <Icon.camera size={18} color="var(--white)"/> {addLabel || 'Tirar foto'}
                  </button>
                  <button className="btn btn-outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    <Icon.file size={18}/> Escolher arquivo
                  </button>
                </div>
                <div style={{ padding: 14, background: 'var(--brand-primary-soft)', borderRadius: 12, marginTop: 20, display: 'flex', gap: 10 }}>
                  <Icon.info size={18} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: 2 }}/>
                  <div style={{ fontSize: 13, color: 'var(--midnight)' }}>
                    <b>Dica:</b> garanta que o pedido esteja legível, com carimbo e assinatura do médico.
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* lista de pedidos enviados */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                  {uploads.map(u => {
                    const offInUpload = (u.exams || []).filter(e => e.available === false).length;
                    return (
                      <div key={u.id} style={{ padding: 12, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                        <button
                          type="button"
                          onClick={() => u.previewUrl && setPreview({ src: u.previewUrl, title: u.name })}
                          disabled={!u.previewUrl}
                          aria-label={u.previewUrl ? 'Ver pedido' : undefined}
                          style={{ width: 46, height: 46, borderRadius: 10, flexShrink: 0, overflow: 'hidden', background: 'var(--marble-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: u.previewUrl ? 'zoom-in' : 'default' }}
                        >
                          {u.previewUrl
                            ? <img src={u.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
                            : <Icon.file size={22} color="var(--brand-primary)"/>}
                        </button>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                            {u.doctor?.name ? `${u.doctor.name} · ` : ''}
                            {(u.exams || []).length === 0
                              ? 'nenhum exame identificado'
                              : `${u.exams.length} exame(s)${offInUpload ? ` · ${offInUpload} não oferecido(s)` : ''}`}
                          </div>
                        </div>
                        <button onClick={() => removeUpload(u.id)} aria-label="Remover pedido" style={{ color: 'var(--text-muted)', padding: 6, flexShrink: 0 }}>
                          <Icon.trash size={18}/>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {fileError && (
                  <div role="alert" style={{ marginTop: 10, padding: '10px 14px', background: 'var(--primary-tint)', borderRadius: 10, color: 'var(--brand-primary)', fontSize: 13, fontWeight: 600 }}>
                    {fileError}
                  </div>
                )}

                {/* adicionar outro pedido */}
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowCamera(true)} disabled={uploading}>
                    <Icon.camera size={16}/> {uploading ? addLabel : 'Tirar foto'}
                  </button>
                  <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => fileRef.current?.click()} disabled={uploading}>
                    <Icon.plus size={16}/> Outro arquivo
                  </button>
                </div>

                {/* leitura automática agregada */}
                <div style={{ marginTop: 20 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                    <Icon.sparkle size={16} color="var(--brand-primary)"/>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>Leitura automática</div>
                    <span className="chip" style={{ background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)' }}>OCR</span>
                  </div>
                  {cart.filter(e => e.fromOrder).length === 0 && (
                    <div role="alert" style={{ padding: '10px 14px', background: 'var(--primary-tint)', borderRadius: 10, color: 'var(--brand-primary)', fontSize: 13, marginBottom: 8 }}>
                      Nenhum exame identificado. Confira se o arquivo é um <b>pedido de exames</b> (receitas de medicamentos não são aceitas) e se a foto está legível.
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      ['Médico', doctorNames.length > 1 ? `${doctorNames.length} médicos` : (doctorNames[0] || '—')],
                      ['Especialidade', uploads.length === 1 ? (uploads[0].doctor?.specialty || '—') : '—'],
                      ['Pedidos enviados', `${uploads.length}`],
                      ['Exames identificados', offCount ? `${cart.filter(e => e.fromOrder).length} exames · ${offCount} não oferecido${offCount > 1 ? 's' : ''}` : `${cart.filter(e => e.fromOrder).length} exames`, true],
                    ].map(([l, v, hl]) => (
                      <div key={l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 10, background: hl ? 'var(--brand-primary-soft)' : 'var(--marble-soft)' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, fontFamily: 'var(--font-display)' }}>{l}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: hl ? 'var(--brand-primary)' : 'var(--midnight)', fontFamily: 'var(--font-display)' }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            <button onClick={() => setDocMode(uploads.length > 0 ? 'rx-ask' : null)} style={{ marginTop: 14, fontSize: 13, color: 'var(--brand-primary)', fontWeight: 600, display: 'block', margin: '14px auto 0' }}>
              Voltar
            </button>
          </div>
        )}
      </div>
      <FooterBar onBack={onBack} onNext={handleContinue} canNext={canNext}
        hint={!canNext ? 'Adicione ao menos um exame para continuar' : null}/>
    </>
  );
}

function CartBar({ cart, total, sourceVariant, linkedOrderCount, onRemove, showAdd, onToggleAdd }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: total > 0 ? 2 : 10 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-display)' }}>
          Sua compra ({cart.length})
        </h3>
        {cart.length > 0 && (
          <button onClick={onToggleAdd} style={{ color: 'var(--brand-primary)', fontSize: 13, fontWeight: 600, display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
            <Icon.plus size={14} color="var(--brand-primary)"/> {showAdd ? 'Fechar' : 'Adicionar'}
          </button>
        )}
      </div>

      {total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
            <span style={{ fontSize: 10.5, color: 'var(--text-subtle)' }}>a partir de</span>
            <b style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--brand-primary)' }}>R$ {brl(total)}</b>
          </span>
        </div>
      )}

      {cart.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-subtle)' }}>Nenhum exame selecionado ainda.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cart.map(e => (
            <div key={e.code} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icon.flask size={15} color="var(--brand-primary)"/>
              <span title={e.name} style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--midnight)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.name}
              </span>
              <SourceTag variant={sourceVariant(e)} orderCount={linkedOrderCount(e)}/>
              {e.price != null && (
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3, flexShrink: 0 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-subtle)' }}>a partir de</span>
                  <b style={{ color: 'var(--brand-primary)', fontSize: 13, fontFamily: 'var(--font-display)' }}>R$ {brl(e.price)}</b>
                </span>
              )}
              <button onClick={() => onRemove(e.code)} style={{ color: 'var(--text-muted)', padding: 2, flexShrink: 0 }}><Icon.x size={14}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExamSearchInline({ cart, onAdd, onClose }) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setMatches([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      getCatalog({ q: term, limit: 8 })
        .then(data => setMatches(data || []))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Icon.search size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
        <input className="input" autoFocus value={q} onChange={e => setQ(e.target.value)}
          placeholder="Buscar exame…" style={{ paddingLeft: 42 }}/>
      </div>
      {onClose && matches.length > 0 && (
        <button onClick={onClose} style={{ marginTop: 10, fontSize: 13, color: 'var(--brand-primary)', fontWeight: 600, display: 'block', marginLeft: 'auto' }}>
          Fechar
        </button>
      )}
      {searching && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>Buscando…</div>}
      {!searching && matches.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {matches.map(e => {
            const already = cart.some(c => c.code === e.code);
            return (
              <button key={e.code} disabled={already} onClick={() => onAdd(e)} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border)',
                background: already ? 'var(--marble-soft)' : 'var(--white)', textAlign: 'left',
                opacity: already ? 0.6 : 1,
              }}>
                <span title={e.name} style={{ fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3, flexShrink: 0 }}>
                  {already ? <span style={{ fontSize: 11, color: 'var(--brand-primary)', fontWeight: 600 }}>Adicionado</span> : (
                    <>
                      <span style={{ fontSize: 9.5, color: 'var(--text-subtle)' }}>a partir de</span>
                      <b style={{ fontSize: 13, color: 'var(--brand-primary)' }}>R$ {brl(e.price)}</b>
                    </>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
